<template>
  <FlotoFixedView>
    <div class="flex-1 overflow-hidden">
      <FlotoContentLoader :loading="loading" class="h-100">
        <SuggestedFieldValuesProvider
          :resource-list="[resource]"
          :module-name="$constants.REQUEST"
        >
          <DetailView
            v-if="resource.id"
            :has-sidebar="hasSidebar"
            :allow-attachments="
              allowedSystemFields.indexOf('fileAttachments') >= 0
            "
            default-sidebar-visible
            :show-requester="false"
            :disabled="isTicketClosed || isParentRequest"
            :resource="resource"
            :module-name="$constants.REQUEST"
            :update-api="handleUpdate"
            class="request-inner-flex"
            :allowed-fields="allowedSystemFields"
          >
            <template v-slot:before-title>
              <FlotoBackButton
                :to="$currentModule.getRoute('my-requests')"
                class="text-neutral-light mr-2"
              />
            </template>
            <template v-slot:title-append>
              <MTooltip v-if="!isTicketClosed && !isParentRequest">
                <template v-slot:trigger>
                  <MButton
                    href="javascript:;"
                    variant="neutral-light"
                    :shadow="false"
                    shape="circle"
                    class="text-neutral md-text-base mx-1"
                    @click="handleEditSubject"
                  >
                    <MIcon name="pencil" />
                  </MButton>
                </template>
                {{ $tc('edit') }}
              </MTooltip>
              <InsertKnowledge
                v-if="allowRequesterToAccessKB"
                :resource="resource"
                disabled
                :title="`${$t('view')} ${$t('knowledge')}`"
              >
                <template v-slot:trigger="{ open }">
                  <MTooltip>
                    <template v-slot:trigger>
                      <MButton
                        variant="neutral-light"
                        class="mx-1"
                        :shadow="false"
                        shape="circle"
                        @click="open"
                      >
                        <MIcon name="lightbulb-on" />
                      </MButton>
                    </template>
                    {{ $t('view') }} {{ $t('knowledge') }}
                  </MTooltip>
                </template>
              </InsertKnowledge>
            </template>
            <template v-slot:actions>
              <div class="flex-col">
                <div class="text-right mb-1 items-center flex justify-end">
                  <RequestRatingView
                    v-if="isFeedbackAllowed && resource.rating > 0"
                    class="mx-1"
                    :value="resource.rating"
                  />
                  <ApprovalStatusIcon
                    v-if="resource.approvalStatus"
                    class="cursor-text mx-1"
                    :status="resource.approvalStatus"
                  >
                    <template v-slot:prefix-text>
                      {{ $tc('approval') }}:
                    </template>
                  </ApprovalStatusIcon>
                  <FlotoStatusPicker
                    v-if="hiddenFields.indexOf('statusId') === -1"
                    v-model="resource.statusId"
                    disabled
                    class="items-center mx-1"
                    :module-name="$constants.REQUEST"
                  />
                  <a
                    v-if="resource.mergedRequest"
                    target="_blank"
                    :href="
                      $router.resolve(
                        $currentModule.getRoute('view-request', {
                          params: { id: resource.id },
                          query: { isParentRequest: true },
                        })
                      ).href
                    "
                  >
                    <MTag
                      class="cursor-pointer"
                      :closable="false"
                      :style="{ maxWidth: '200px' }"
                      variant="neutral-lighter"
                    >
                      {{ $tc('merged_with') }}:
                      {{ resource.parentRequestName }}
                    </MTag>
                  </a>
                  <MButton
                    v-if="canReopen"
                    id="reopen-btn"
                    class="mx-1"
                    @click="handleReopenTicket"
                  >
                    {{ $t('reopen') }} {{ $tc($constants.REQUEST) }}
                  </MButton>
                  <MButton
                    v-if="
                      !isTicketClosed && !isParentRequest && allowToCloseReq
                    "
                    :id="`close-${$constants.REQUEST}`"
                    class="mx-1"
                    @click="handleCloseTicket"
                  >
                    {{ $t('close') }} {{ $tc($constants.REQUEST) }}
                  </MButton>
                  <MButton
                    v-if="showFeedbackButton"
                    id="feedback"
                    class="mx-1"
                    @click="isFeedbackDrawerOpen = true"
                  >
                    {{ $t('feedback') }}
                  </MButton>
                  <ActionMenu
                    id="action-menu"
                    :module-name="$constants.REQUEST"
                    :resource-id="resource.id"
                    :resource="resource"
                  />
                </div>
                <small
                  v-if="resource.resolvedAt"
                  class="text-neutral"
                  style="float: right"
                >
                  {{ $t('last_resolved_time') }}:
                  {{ resource.resolvedAt | datetime }}
                  ({{ resource.resolvedAt | timeago }})
                </small>
              </div>
            </template>
            <template v-slot:additional-fields>
              <AdditionalInfo
                :key="additionalInfoCount"
                disabled
                :module-name="$constants.REQUEST"
                :is-form-rules-available="
                  moduleName === $constants.REQUEST &&
                  (formRulesContext.hasTicketFormRules ||
                    formRulesContext.hasServiceCatalogFormRules)
                "
                :resource="resource"
                :update-fn="(e) => e"
              />
            </template>
            <template v-slot:primary-row>
              <span />
            </template>
            <template v-slot:secondary-row>
              <span />
            </template>
            <template v-slot:tags-row>
              <span />
            </template>
            <!-- system fields as details -->
            <template v-slot:sidebar>
              <div class="overflow-x-hidden">
                <Sidebar
                  v-model="sidebarInfo"
                  :disabled="disabled"
                  :allowed-fields="allowedSidebarFields"
                  :module-name="$constants.REQUEST"
                  :resource="resource"
                  :hidden-fields="hiddenFields"
                  :is-required-fn="isRequiredFn"
                  :single-field-rules-state-fn="singleFieldRulesStateFn"
                >
                  <template v-slot:before>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('statusId') >= 0 &&
                        hiddenFields.indexOf('statusId') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required': isRequiredFn('statusId'),
                            }"
                          >
                            {{ $tc('status') }}
                          </small>
                        </div>
                        <div>
                          <FlotoStatusPicker
                            :value="resource.statusId"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            as-input
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('statusId').disable
                            "
                            :allow-clear="false"
                            :module-name="$constants.REQUEST"
                            :placeholder="disabled ? '---' : undefined"
                            class="w-full"
                            v-bind="{
                              hiddenOptionsKeys:
                                singleFieldRulesStateFn('statusId')
                                  .hiddenOptionsKeys || [],
                              visibleOptionsKeys:
                                singleFieldRulesStateFn('statusId')
                                  .visibleOptionsKeys || [],
                            }"
                            @change="handleUpdate({ statusId: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('priorityId') >= 0 &&
                        hiddenFields.indexOf('priorityId') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required':
                                isRequiredFn('priorityId'),
                            }"
                          >
                            {{ $tc('priority') }}
                          </small>
                        </div>
                        <div>
                          <FlotoPriorityPicker
                            :value="resource.priorityId"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            as-input
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('priorityId').disable
                            "
                            :allow-clear="false"
                            :placeholder="disabled ? '---' : undefined"
                            class="w-full"
                            v-bind="{
                              hiddenOptionsKeys:
                                singleFieldRulesStateFn('priorityId')
                                  .hiddenOptionsKeys || [],
                              visibleOptionsKeys:
                                singleFieldRulesStateFn('priorityId')
                                  .visibleOptionsKeys || [],
                            }"
                            @change="handleUpdate({ priorityId: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('urgencyId') >= 0 &&
                        hiddenFields.indexOf('urgencyId') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required': isRequiredFn('urgencyId'),
                            }"
                          >
                            {{ $tc('urgency') }}
                          </small>
                        </div>
                        <div>
                          <FlotoUrgencyPicker
                            :value="resource.urgencyId"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            as-input
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('urgencyId').disable
                            "
                            :allow-clear="false"
                            :placeholder="disabled ? '---' : undefined"
                            class="w-full"
                            v-bind="{
                              hiddenOptionsKeys:
                                singleFieldRulesStateFn('urgencyId')
                                  .hiddenOptionsKeys || [],
                              visibleOptionsKeys:
                                singleFieldRulesStateFn('urgencyId')
                                  .visibleOptionsKeys || [],
                            }"
                            @change="handleUpdate({ urgencyId: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('impactId') >= 0 &&
                        hiddenFields.indexOf('impactId') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required': isRequiredFn('impactId'),
                            }"
                          >
                            {{ $tc('impact') }}
                          </small>
                        </div>
                        <div>
                          <FlotoImpactPicker
                            :value="resource.impactId"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            :allow-clear="false"
                            as-input
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('impactId').disable
                            "
                            :placeholder="disabled ? '---' : undefined"
                            class="w-full"
                            v-bind="{
                              hiddenOptionsKeys:
                                singleFieldRulesStateFn('impactId')
                                  .hiddenOptionsKeys || [],
                              visibleOptionsKeys:
                                singleFieldRulesStateFn('impactId')
                                  .visibleOptionsKeys || [],
                            }"
                            @change="handleUpdate({ impactId: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('groupId') >= 0 &&
                        hiddenFields.indexOf('groupId') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required': isRequiredFn('groupId'),
                            }"
                          >
                            {{ $tc('technician_group') }}
                          </small>
                        </div>
                        <div>
                          <FlotoTechnicianGroupPicker
                            :value="resource.groupId"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            as-input
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('groupId').disable
                            "
                            :mandatory-selection="isRequiredFn('groupId')"
                            :allow-clear="!isRequiredFn('groupId')"
                            :placeholder="disabled ? '---' : undefined"
                            class="w-full"
                            v-bind="{
                              hiddenOptionsKeys:
                                singleFieldRulesStateFn('groupId')
                                  .hiddenOptionsKeys || [],
                              visibleOptionsKeys:
                                singleFieldRulesStateFn('groupId')
                                  .visibleOptionsKeys || [],
                            }"
                            @change="handleUpdate({ groupId: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('technicianId') >= 0 &&
                        hiddenFields.indexOf('technicianId') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required':
                                isRequiredFn('technicianId'),
                            }"
                          >
                            {{ $tc('assigned_to') }}
                          </small>
                        </div>
                        <div>
                          <FlotoTechnicianPicker
                            :value="resource.technicianId"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            as-input
                            :group-id="resource.groupId"
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('technicianId').disable
                            "
                            :mandatory-selection="isRequiredFn('technicianId')"
                            :allow-clear="!isRequiredFn('technicianId')"
                            :placeholder="disabled ? '---' : undefined"
                            class="w-full"
                            v-bind="{
                              hiddenOptionsKeys:
                                singleFieldRulesStateFn('technicianId')
                                  .hiddenOptionsKeys || [],
                              visibleOptionsKeys:
                                singleFieldRulesStateFn('technicianId')
                                  .visibleOptionsKeys || [],
                            }"
                            @change="handleUpdate({ technicianId: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                    <MRow
                      v-if="
                        allowedSystemFields.indexOf('tags') >= 0 &&
                        hiddenFields.indexOf('tags') === -1
                      "
                      class="my-3"
                    >
                      <MCol>
                        <div>
                          <small
                            class="text-neutral-light"
                            :class="{
                              'field-lable-required': isRequiredFn('tags'),
                            }"
                          >
                            {{ $tc('tag', 2) }}
                          </small>
                        </div>
                        <div class="bordered-bottom">
                          <FlotoTagsPicker
                            :value="resource.tags"
                            :class="{
                              'read-only': disabled,
                              'material-input': !disabled,
                            }"
                            :disabled="
                              disabled ||
                              singleFieldRulesStateFn('tags').disable
                            "
                            :required="isRequiredFn('tags')"
                            class="w-full mb-2"
                            @change="handleUpdate({ tags: $event })"
                          />
                        </div>
                      </MCol>
                    </MRow>
                  </template>
                </Sidebar>
              </div>
            </template>
            <!-- default content as tabs -->
            <template v-slot>
              <div :key="tabsRerenderCount" class="tabs mt-4">
                <MAffix>
                  <MTab v-model="activeTabKey" size="small" class="affixed-tab">
                    <MTabPane
                      v-for="tab in allowedTabs"
                      :key="tab.id"
                      :tab="tab.name"
                    />
                  </MTab>
                </MAffix>
                <div class="my-4 mr-6">
                  <ConversationContainer
                    v-if="activeTabKey === 'work'"
                    :resource-id="resource.id"
                    :resource="resource"
                    :allowed-types="['requestor_to_technician', 'solution']"
                    :excluded-types="[]"
                    :module-name="$constants.REQUEST"
                    :can-create="!isParentRequest"
                  />
                  <RequestInformationTab
                    v-if="activeTabKey === 'request_information'"
                    :resource="resource"
                    disabled
                  />
                  <LinkedAssetTab
                    v-if="
                      activeTabKey === 'linked_asset' && isLinkAssetTabAllowed
                    "
                    :key="activeTabKey"
                    :module-name="$constants.ASSET"
                    :resource-id="resource.id"
                    :disabled="disabled"
                    :service-catalog-id="resource.serviceCatalogId"
                  />
                  <LinkedAssetTab
                    v-if="activeTabKey === 'linked_ci' && isLinkCiTabAllowed"
                    :key="activeTabKey"
                    :module-name="$constants.CMDB"
                    :resource-id="resource.id"
                    :disabled="disabled"
                    :service-catalog-id="resource.serviceCatalogId"
                  />
                  <ApprovalContainer
                    v-if="activeTabKey === 'approval'"
                    :resource-id="resource.id"
                    disabled
                    allow-comment-on-approval
                    :module-name="$constants.REQUEST"
                  />
                  <ConversationContainer
                    v-if="activeTabKey === 'feedback'"
                    :key="activeTabKey"
                    :resource-id="resource.id"
                    disabled
                    :allowed-types="['feedback']"
                    :excluded-types="[]"
                    :module-name="$constants.REQUEST"
                    :searchable="false"
                    :fetch-fn="fetchLatestFeedback"
                  />
                  <AuditContainer
                    v-if="
                      activeTabKey === 'activity' &&
                      allowRequesterToViewAuditTrail
                    "
                    :module-name="$constants.REQUEST"
                    :resource-id="resource.id"
                  />
                </div>
              </div>
            </template>
          </DetailView>
        </SuggestedFieldValuesProvider>
      </FlotoContentLoader>
    </div>
    <!-- confirm modal for if ticket has secondarytickets and it gets closed -->
    <ConfirmCloseModal
      v-if="showConfirmCloseTicketModal"
      :module-name="$constants.REQUEST"
      @confirm="handleUpdate($event, true)"
      @hide="showConfirmCloseTicketModal = false"
    />
    <!-- Note required when mandate comment on reopen enabled -->
    <NoteRules ref="noteDrawer" :create-fn="handleNoteAdded">
      <template v-slot:header>
        {{ $tc('comment_required_for_changes') }}
      </template>
    </NoteRules>
    <FeedbackFormDrawer
      :resource="resource"
      :is-feedback-drawer-open="isFeedbackDrawerOpen"
      @done="getRequestDetail"
      @handleFeedbackDrawer="handleFeedbackDrawer"
    />
    <FlotoDrawerForm
      v-if="!disabled"
      :open="isEditingSubject"
      @cancel="isEditingSubject = false"
      @submit="handleUpdateSubject"
    >
      <template v-slot:header> {{ $t('edit') }}: {{ resource.name }} </template>
      <FlotoFormItem
        v-model="internalValueModel.subject"
        :label="$t('subject')"
        :placeholder="$t('subject')"
        rules="required"
      />
      <FlotoFormItem
        v-if="allowedSystemFields.indexOf('description') >= 0"
        :label="$t('description')"
      >
        <FlotoRichEditor
          v-model="internalValueModel.description"
          :affix-toolbar="false"
          :placeholder="$t('description')"
        />
      </FlotoFormItem>
      <FlotoFormItem
        v-if="allowedSystemFields.indexOf('fileAttachments') >= 0"
        :label="$tc('attachment', 2)"
      >
        <FlotoAttachment
          v-model="internalValueModel.fileAttachments"
          @upload-start="updatingSubject = true"
          @upload-done="updatingSubject = false"
        />
      </FlotoFormItem>
      <FormConsumer
        v-if="customFields.length"
        v-model="internalValueModel.fieldValueDetails"
        :module-name="$constants.REQUEST"
        :form-fields="customFields"
        avoid-default-value
        :with-submit="false"
      />
      <template v-slot:actions="{ submit, hide }">
        <MButton :loading="updatingSubject" @click="submit">
          {{ $t('update') }}
        </MButton>
        <MButton variant="default" class="ml-2" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawerForm>
    <TicketFormEditModal
      v-if="formRulesContext.showEditFormModal"
      :un-touched-resource="resource"
      :module-name="$constants.REQUEST"
      :resource="formRulesContext.updatedResource"
      :open="formRulesContext.showEditFormModal"
      :submit-fn="handleSubmitEditFormFn"
      @cancel="handleCancel"
    />
  </FlotoFixedView>
</template>

<script>
import Pick from 'lodash/pick'
import Moment from 'moment'
import {
  getApi,
  updateApi,
  getMergedRequestApi,
} from '@modules/ticket/ticket-api'
import { authComputed } from '@state/modules/auth'
import { FormComputed } from '@state/modules/form'
import { StatusComputed } from '@state/modules/status'
import DetailView from '@modules/ticket/components/detail/detail'
import Sidebar from '@modules/ticket/components/detail/sidebar'
import ConversationContainer from '@components/conversation/conversation-container'
import AdditionalInfo from '@components/additional-information/additional-info'
import ConfirmCloseModal from '@modules/ticket/components/confirm-close-modal'
import RequestRatingView from '@modules/ticket/components/request-rating-view'
import RequestInformationTab from '@modules/ticket/components/request-information-tab'
import AuditContainer from '@components/audit/audit-container'
import FormConsumer from '@components/form-consumer.vue'
import {
  SupportPortalConfigComputed,
  SupportPortalConfigMethods,
} from '@state/modules/support-portal-config'
import { PreferenceComputed } from '@state/modules/preference'
import LinkedAssetTab from '../components/linked-asset-tab/relation-container'
import FeedbackFormDrawer from '../components/feedback-form-drawer'
import { getFeedbackSettings, getLatestFeedback } from '../support-portal-api'
import { getFieldByRequesterAccess } from '../helpers/field-access'
import ApprovalStatusIcon from '@components/approval/components/approval-status-icon'
import { executionTypeMap, userTypeMap, eventTypeMap } from '@data/form-rules'
import TicketFormEditModal from '@modules/ticket/components/ticket-form-edit-modal.vue'
import ApprovalContainer from '@components/approval/approval-container'
import SuggestedFieldValuesProvider from '@components/providers/suggested-field-value-provider'
import { getGlobalUserApi } from '@modules/users/users-api'
import { getServiceCatalogApi } from '@modules/service-catalog/service-catalog-api'
import NoteRules from '@src/components/note-rules/note-rules.vue'
import InsertKnowledge from '@src/components/conversation/insert-knowledge.vue'
import ActionMenu from '../components/action-menu'

export default {
  name: 'RequestDetail',
  components: {
    DetailView,
    Sidebar,
    ConfirmCloseModal,
    ConversationContainer,
    RequestInformationTab,
    LinkedAssetTab,
    FeedbackFormDrawer,
    AdditionalInfo,
    FormConsumer,
    ApprovalStatusIcon,
    TicketFormEditModal,
    ApprovalContainer,
    SuggestedFieldValuesProvider,
    AuditContainer,
    NoteRules,
    RequestRatingView,
    InsertKnowledge,
    ActionMenu,
  },
  inject: ['formRulesContext'],
  data() {
    this.moduleName = this.$constants.REQUEST
    return {
      pendingChanges: null,
      loading: false,
      resource: {},
      allowRequesterToLinkMyAsset: false,
      allowRequesterToLinkMyCi: false,
      requesterDetails: {},
      showConfirmCloseTicketModal: false,
      allowedTabs: [],
      activeTabKey: 'work',
      feedbackData: {},
      isFeedbackDrawerOpen: false,
      isEditingSubject: false,
      updatingSubject: false,
      internalValueModel: {},
      additionalInfoCount: 1,
      parentRequestDetails: {},
      tabsRerenderCount: 1,
    }
  },

  computed: {
    ...authComputed,
    ...FormComputed,
    ...StatusComputed,
    ...SupportPortalConfigComputed,
    ...PreferenceComputed,
    customFields() {
      const fields = this[`${this.$constants.REQUEST}Fields`]({
        exclude: ['system', 'api'],
      }).filter((f) => f.useOnPortal)
      return getFieldByRequesterAccess(fields, this.user)
    },
    hasSidebar() {
      return Boolean(
        this.allowedSidebarFields.filter((c) => c !== 'description').length
      )
    },
    canReopen() {
      if (
        this.isTicketClosed &&
        this.allowToReopenClosedReq &&
        !this.isParentRequest
      ) {
        if (this.closedGraceTimeType === 'unlimited') {
          return true
        } else {
          return (
            Moment().unix() * 1000 <
            Moment(this.resource.closedAt)
              .add(this.closedGraceTime, 'days')
              .unix() *
              1000
          )
        }
      }
      if (this.isTicketResolved && this.allowToReopenResolvedReq) {
        if (this.resolvedGraceTimeType === 'unlimited') {
          return true
        } else {
          return (
            Moment().unix() * 1000 <
            Moment(this.resource.resolvedAt)
              .add(this.resolvedGraceTime, 'days')
              .unix() *
              1000
          )
        }
      }
      return false
    },
    // @TODO - Used the status obtained instead of currentReadOnlyStatus , computed code can be reusable

    // currentReadOnlyStatus() {
    //   const excludedStatusIds = [
    //     this.closeStatusId,
    //     this.openStatusId,
    //     this.resolvedStatusId,
    //   ]
    //   if (excludedStatusIds.indexOf(this.resource.statusId) >= 0) {
    //     return this.resource.statusId
    //   }
    //   const allStatus = this.requestStatus
    //   const currentStatus = allStatus.find(
    //     (s) => s.key === this.resource.statusId
    //   )
    //   if (currentStatus) {
    //     if (currentStatus.runSla) {
    //       return (this.requestFindStatus('in progress') || {}).id
    //     } else {
    //       return (this.requestFindStatus('pending') || {}).id
    //     }
    //   }
    //   return undefined
    // },
    isParentRequest() {
      return this.$route.query.isParentRequest
    },
    disabled() {
      return (
        this.resource.statusId === this.closeStatusId || this.isParentRequest
      )
    },
    isFeedbackAllowed() {
      const { sendOnClosed, sendOnResolved } = this.feedbackData
      if (this.allowRequesterToSubmitFeedback) {
        if (this.isTicketClosed && sendOnClosed && !this.isParentRequest) {
          return true
        } else if (this.isTicketResolved && sendOnResolved) {
          return true
        }
      }
      return false
    },

    closeStatusId() {
      if (this.requestFindStatus) {
        return (this.requestFindStatus('closed') || {}).id
      }
      return undefined
    },
    openStatusId() {
      if (this.requestFindStatus) {
        return (this.requestFindStatus('open') || {}).id
      }
      return undefined
    },
    resolvedStatusId() {
      if (this.requestFindStatus) {
        return (this.requestFindStatus('resolved') || {}).id
      }
      return undefined
    },
    isTicketClosed() {
      return this.resource.statusId === this.closeStatusId
    },
    isTicketResolved() {
      return this.resource.statusId === this.resolvedStatusId
    },
    allowedSystemFields() {
      const portalFields = getFieldByRequesterAccess(
        this.requestPortalFields,
        this.user
      )
      return (
        portalFields
          .filter((f) => f.isSystemField)
          .map((f) => f.paramName)
          // refactor requester_email to requester
          .filter((p) => ['requester', 'subject'].indexOf(p) === -1)
      )
    },
    allowedSidebarFields() {
      return this.allowedSystemFields.filter((c) => c !== 'fileAttachments')
    },
    sidebarInfo: {
      get() {
        return Pick(this.resource, [
          'createdAt',
          'updatedAt',
          'categoryId',
          'supportLevel',
          'sourceId',
          'locationId',
          'vendorId',
          'escalationLevel',
          'escalationResponseLevel',
          'resolvedAt',
          'closedAt',
          'ccEmailSet',
          'estimatedTime',
          'departmentId',
          'templateId',
          'fileAttachments',
          'requestType',
          'resolutionTime',
        ])
      },
      set(value) {
        this.handleUpdate(value)
      },
    },
    hiddenFields() {
      const hiddenFormRulesFields = this.isFormRulesAvailable
        ? Object.keys(this.formRulesContext.fieldRulesState || {}).filter(
            (key) => {
              return (this.formRulesContext.fieldRulesState || {})[key].hide
            }
          )
        : []
      return hiddenFormRulesFields
    },
    isFormRulesAvailable() {
      if (
        this.moduleName === this.$constants.REQUEST &&
        (this.formRulesContext.hasTicketFormRules ||
          this.formRulesContext.hasServiceCatalogFormRules)
      ) {
        return true
      }
      return false
    },
    formRulesUserTypeFilter() {
      return [
        userTypeMap['all'],
        userTypeMap['requester'],
        userTypeMap['logged_in_user'],
      ]
    },
    isLinkAssetTabAllowed() {
      if (
        this.resource.requestType === 'service_request' &&
        this.allowRequesterToLinkMyAsset
      ) {
        return true
      } else if (
        this.resource.requestType === 'incident' &&
        this.allowToLinkAsset
      ) {
        return true
      }
      return false
    },
    isLinkCiTabAllowed() {
      if (
        this.resource.requestType === 'service_request' &&
        this.allowRequesterToLinkMyCi
      ) {
        return true
      } else if (
        this.resource.requestType === 'incident' &&
        this.allowToLinkCi
      ) {
        return true
      }
      return false
    },
    showFeedbackButton() {
      if (
        this.feedbackData.allowOnlyOneFeedback &&
        this.resource.rating > 0 &&
        this.isFeedbackAllowed
      ) {
        return false
      }
      return this.isFeedbackAllowed
    },
  },
  watch: {
    allowRequesterToSubmitFeedback: {
      immediate: true,
      handler: 'setTabs',
    },
    allowRequesterToLinkMyAsset: {
      immediate: true,
      handler: 'setTabs',
    },
    allowRequesterToLinkMyCi: {
      immediate: true,
      handler: 'setTabs',
    },
    resource: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.handleExectuteFormRules()
        }
      },
    },
    isFormRulesAvailable: {
      immediate: true,
      handler: 'handleExectuteFormRules',
    },
  },
  created() {
    getFeedbackSettings().then((data) => {
      this.feedbackData = data
    })
    this.getRequestDetail()
  },
  methods: {
    ...SupportPortalConfigMethods,
    handleExectuteFormRules() {
      if (this.isFormRulesAvailable && Object.keys(this.resource).length) {
        const executeFn = (requesterDetails) => {
          this.formRulesContext.executeFormRules(this.resource, {
            executionTypes: [
              executionTypeMap['create_and_edit'],
              executionTypeMap['edit'],
            ],
            userTypes: this.formRulesUserTypeFilter,
            eventType: eventTypeMap['load'],
            requesterDetails,
          })
        }
        if (Object.keys(this.requesterDetails).length) {
          executeFn(this.requesterDetails)
        } else {
          getGlobalUserApi(this.resource.requesterId, { archived: true }).then(
            (data) => {
              this.requesterDetails = data
              executeFn(data)
            }
          )
        }
      }
    },
    getRequestDetail() {
      const apiFn = this.$route.query.isParentRequest
        ? getMergedRequestApi
        : getApi
      apiFn(this.$constants.REQUEST, this.$route.params.id, true)
        .then((data) => {
          this.resource = data
          this.setTabs()
          if (data.serviceCatalogId) {
            getServiceCatalogApi(
              data.serviceCatalogId,
              {
                archived: true,
                skipValidate: true,
              },
              this.isPortalLogin
            ).then((data) => {
              this.allowRequesterToLinkMyAsset =
                data.allowRequesterToLinkMyAsset
              this.allowRequesterToLinkMyCi = data.allowRequesterToLinkMyCi
            })
            this.formRulesContext.fetchServiceCatalogFormRules(
              data.serviceCatalogId
            )
          }
          this.loading = false
        })
        .catch(() => {
          this.$router.replace({ name: '404' })
        })
    },
    setTabs() {
      const tabs = [
        {
          id: 'work',
          name: this.$tc('comment', 2),
        },
        ...(this.isLinkAssetTabAllowed
          ? [
              {
                id: 'linked_asset',
                name: `${this.$tc('linked')} ${this.$tc('asset', 2)}`,
              },
            ]
          : []),
        ...(this.isLinkCiTabAllowed
          ? [
              {
                id: 'linked_ci',
                name: `${this.$tc('linked')} ${this.$tc('ci', 2)}`,
              },
            ]
          : []),
        ...(this.resource.serviceCatalogId
          ? [
              {
                id: 'request_information',
                name: `${this.$tc('request')} ${this.$t('information')}`,
              },
            ]
          : []),
        ...(this.isFeedbackAllowed && this.resource.rating
          ? [
              {
                id: 'feedback',
                name: `${this.$tc('feedback')}`,
              },
            ]
          : []),
        ...(this.allowRequesterToAccestTicketApprovalTab &&
        this.resource.approvalStatus &&
        this.resource.approvalStatus !== 'pre_approved'
          ? [
              {
                id: 'approval',
                name: `${this.$tc('approval')}`,
              },
            ]
          : []),
        ...(this.allowRequesterToViewAuditTrail
          ? [
              {
                id: 'activity',
                name: this.$tc('audit_trail'),
              },
            ]
          : []),
      ]
      this.allowedTabs = Object.freeze(tabs)
    },

    handleUpdate(change, forceUpdate = false, checkFormRules = true) {
      if (Object.keys(change).length) {
        // check form rules
        if (
          checkFormRules &&
          this.formRulesContext.isFormRuleQualified({
            executionTypes: [
              executionTypeMap['create_and_edit'],
              executionTypeMap['edit'],
            ],
            userTypes: this.formRulesUserTypeFilter,
            eventType: eventTypeMap['change'],
            triggerFields: Object.keys(change),
          })
        ) {
          this.formRulesContext.applyFormRules(
            { ...this.resource, ...change },
            {},
            {
              executionTypes: [
                executionTypeMap['create_and_edit'],
                executionTypeMap['edit'],
              ],
              userTypes: this.formRulesUserTypeFilter,
              eventType: eventTypeMap['change'],
              triggerFields: Object.keys(change),
            }
          )
          return
        }
        if (
          forceUpdate !== true &&
          'statusId' in change &&
          change.statusId === this.closeStatusId &&
          checkFormRules
        ) {
          this.showConfirmCloseTicketModal = true
          return Promise.resolve()
        }
        return updateApi(
          this.$constants.REQUEST,
          {
            id: this.resource.id,
            ...change,
          },
          true
        ).then((data) => {
          this.resource = { ...this.resource, ...data }
          return data
        })
      }
      return Promise.resolve()
    },
    handleReopenTicket() {
      if (this.mandateCommentToReopenRequest) {
        this.pendingChanges = { statusId: this.openStatusId }
        this.$refs.noteDrawer.openForm()
      } else {
        this.handleUpdate({ statusId: this.openStatusId }, false, false)
      }
    },
    handleNoteAdded(data) {
      return this.handleUpdate(
        {
          ...data,
          ...this.pendingChanges,
        },
        true,
        false
      ).then(() => {
        this.$refs.noteDrawer.closeForm()
        this.refreshTicket(['work'])
      })
    },
    refreshTicket(reRenderTabs = undefined) {
      return getApi(this.moduleName, this.resource.id).then((ticket) => {
        this.resource = ticket
        if (reRenderTabs && reRenderTabs.indexOf(this.activeTabKey) >= 0) {
          this.tabsRerenderCount = this.tabsRerenderCount + 1
        }
        this.setTabs()
        return ticket
      })
    },
    handleCloseTicket() {
      this.handleUpdate({ statusId: this.closeStatusId }, false, true)
    },
    handleFeedbackDrawer() {
      this.isFeedbackDrawerOpen = false
      this.activeTabKey = 'work'
    },
    handleEditSubject() {
      this.formRulesContext.handleShowEditFormModal(this.resource)

      // this.internalValueModel = Pick(this.resource, [
      //     'subject',
      //     'description',
      //     'fileAttachments',
      //     'fieldValueDetails',
      //   ])
      //   this.isEditingSubject = true
    },
    handleUpdateSubject() {
      this.updatingSubject = true
      this.handleUpdate(this.internalValueModel)
        .then(() => {
          this.internalValueModel = {}
          this.isEditingSubject = false
          this.additionalInfoCount++
        })
        .finally(() => (this.updatingSubject = false))
    },
    fetchLatestFeedback(limit, offset) {
      return getLatestFeedback(
        this.$constants.REQUEST,
        this.resource.id,
        limit,
        offset
      )
    },
    handleSubmitEditFormFn(change) {
      return this.handleUpdate(change, false, false).then(() => {
        this.formRulesContext.handleHideEditFormModal()
        this.formRulesContext.setInitState()
        this.handleExectuteFormRules()
        this.additionalInfoCount++
      })
    },
    handleCancel() {
      this.formRulesContext.handleHideEditFormModal()
      this.formRulesContext.setInitState()
      this.handleExectuteFormRules()
    },
    isRequiredFn(paramName) {
      const fields = this.requestForm.fields
      const currentField = (fields || []).find((f) => f.paramName === paramName)
      const formRulesMandatoryField = this.isFormRulesAvailable
        ? (this.formRulesContext.fieldRulesState || {})[paramName].mandatory
        : false
      if (this.isPortalLogin) {
        return (currentField || {}).requesterRequired || formRulesMandatoryField
      } else {
        return (currentField || {}).required || formRulesMandatoryField
      }
    },
    singleFieldRulesStateFn(paramName) {
      if (!paramName) {
        return {}
      }
      const fields = this.requestForm.fields
      const currentField = (fields || []).find((f) => f.paramName === paramName)
      if (this.isPortalLogin) {
        const prepairedRuleState = {
          ...(this.formRulesContext.fieldRulesState || {}),
          [paramName]: {
            ...((this.formRulesContext.fieldRulesState || {})[paramName] || {}),
            disable:
              !(currentField || {}).requesterCanEdit ||
              ((this.formRulesContext.fieldRulesState || {})[paramName] || {})
                .disable,
          },
        }
        return (prepairedRuleState || {})[paramName] || {}
      } else {
        return (this.formRulesContext.fieldRulesState || {})[paramName] || {}
      }
    },
  },
}
</script>

<style lang="less" scoped>
.bordered-bottom {
  border-bottom: 1px solid var(--border-color);
}
</style>
